import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWebhookEvents } from '../api/claw9';

// Mock customer data (used when no accountId or in demo mode)
const MOCK_CUSTOMERS = [
  { id: 'cust_1', name: 'Andrew Nguyen', avatar: null, channel: 'zalo' },
  { id: 'cust_2', name: 'Minh Tran', avatar: null, channel: 'zalo' },
  { id: 'cust_3', name: 'Linh Pham', avatar: null, channel: 'telegram' },
  { id: 'cust_4', name: 'Duc Le', avatar: null, channel: 'facebook' },
  { id: 'cust_5', name: 'Hoa Vo', avatar: null, channel: 'whatsapp' },
  { id: 'cust_6', name: 'Nam Hoang', avatar: null, channel: 'zalo' },
  { id: 'cust_7', name: 'Thao Dang', avatar: null, channel: 'zalo' },
  { id: 'cust_8', name: 'Khanh Bui', avatar: null, channel: 'telegram' },
];

const MOCK_MESSAGES = [
  'Cho mình hỏi về gói dịch vụ',
  'Giá cả như thế nào ạ?',
  'Có hỗ trợ watermark/logo không?',
  'Mình muốn đặt lịch tư vấn',
  'Cảm ơn bạn nhé!',
  'Bao lâu thì nhận được video?',
  'Có giảm giá cho đơn hàng lớn không?',
  'Mình cần video quảng cáo sản phẩm',
];

const REAL_POLL_INTERVAL_MS = 5000;
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;

export const EVENT_TYPES = {
  NEW_MESSAGE: 'new_message',
  AGENT_RESPONDING: 'agent_responding',
  AGENT_RESPONDED: 'agent_responded',
  CONVERSATION_CLOSED: 'conversation_closed',
  CUSTOMER_WAITING: 'customer_waiting',
};

const EVENT_TYPE_ALIASES = {
  new_message: EVENT_TYPES.NEW_MESSAGE,
  incoming_message: EVENT_TYPES.NEW_MESSAGE,
  inbound_message: EVENT_TYPES.NEW_MESSAGE,
  message_received: EVENT_TYPES.NEW_MESSAGE,
  customer_message: EVENT_TYPES.NEW_MESSAGE,
  agent_responding: EVENT_TYPES.AGENT_RESPONDING,
  responding: EVENT_TYPES.AGENT_RESPONDING,
  response_started: EVENT_TYPES.AGENT_RESPONDING,
  agent_responded: EVENT_TYPES.AGENT_RESPONDED,
  agent_replied: EVENT_TYPES.AGENT_RESPONDED,
  response_sent: EVENT_TYPES.AGENT_RESPONDED,
  message_sent: EVENT_TYPES.AGENT_RESPONDED,
  outbound_message: EVENT_TYPES.AGENT_RESPONDED,
  conversation_closed: EVENT_TYPES.CONVERSATION_CLOSED,
  closed: EVENT_TYPES.CONVERSATION_CLOSED,
  customer_waiting: EVENT_TYPES.CUSTOMER_WAITING,
  waiting: EVENT_TYPES.CUSTOMER_WAITING,
};

const ACTIVE_EVENT_TYPES = new Set([
  EVENT_TYPES.NEW_MESSAGE,
  EVENT_TYPES.AGENT_RESPONDING,
  EVENT_TYPES.CUSTOMER_WAITING,
]);

const stableHash = (value) => String(value || '')
  .split('')
  .reduce((total, char) => total + char.charCodeAt(0), 0);

const normalizeEventType = (rawType) => {
  const normalized = String(rawType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return EVENT_TYPE_ALIASES[normalized] || EVENT_TYPES.NEW_MESSAGE;
};

const resolveAgent = (rawEvent, agents) => {
  const agentSource = rawEvent.agent || {};
  const explicitId = agentSource.id || rawEvent.agent_id || rawEvent.agentId;
  if (explicitId) {
    const matchedAgent = agents.find((agent) => agent.id === explicitId || agent.name === explicitId);
    return matchedAgent
      ? { id: matchedAgent.id, name: matchedAgent.name }
      : { id: explicitId, name: agentSource.name || explicitId };
  }

  if (!agents.length) {
    return null;
  }

  const customerSource = rawEvent.customer || rawEvent.contact || rawEvent.lead || {};
  const rawData = rawEvent.data || rawEvent.payload || {};
  const seed = rawData.conversationId
    || rawData.conversation_id
    || rawEvent.conversationId
    || rawEvent.conversation_id
    || customerSource.id
    || rawEvent.id;
  const agent = agents[stableHash(seed) % agents.length];
  return agent ? { id: agent.id, name: agent.name } : null;
};

const normalizeEvent = (rawEvent, agents) => {
  if (!rawEvent || typeof rawEvent !== 'object') {
    return null;
  }

  const rawData = rawEvent.data || rawEvent.payload || {};
  const customerSource = rawEvent.customer || rawEvent.contact || rawEvent.lead || {};
  const type = normalizeEventType(rawEvent.type || rawEvent.event_type || rawEvent.eventType);
  const timestamp = rawEvent.timestamp || rawEvent.created_at || rawEvent.createdAt || new Date().toISOString();
  const customerId = customerSource.id || rawEvent.customer_id || rawEvent.customerId || rawData.customerId || rawData.customer_id || null;
  const conversationId = rawData.conversationId
    || rawData.conversation_id
    || rawEvent.conversationId
    || rawEvent.conversation_id
    || null;
  const responseTime = Number(
    rawData.responseTime
      ?? rawData.response_time
      ?? rawEvent.responseTime
      ?? rawEvent.response_time
      ?? 0
  ) || 0;
  const agent = resolveAgent(rawEvent, agents);
  const id = rawEvent.id
    || rawEvent.event_id
    || `${type}:${conversationId || customerId || 'unknown'}:${timestamp}`;

  return {
    id,
    type,
    timestamp,
    customer: {
      id: customerId,
      name: customerSource.name || customerSource.full_name || customerSource.display_name || 'Unknown',
      avatar: customerSource.avatar || customerSource.picture || null,
      channel: customerSource.channel || rawEvent.channel || rawEvent.platform || rawData.channel || 'zalo',
    },
    agent,
    data: {
      ...rawData,
      message: rawData.message || rawData.text || rawEvent.message || rawEvent.text || '',
      conversationId,
      responseTime,
    },
  };
};

const sortEventsDesc = (items) => [...items].sort(
  (left, right) => new Date(right.timestamp || 0).getTime() - new Date(left.timestamp || 0).getTime()
);

const buildActiveEvents = (normalizedEvents) => {
  const active = {};
  const now = Date.now();

  normalizedEvents.forEach((event) => {
    if (!event.agent?.id) {
      return;
    }

    const eventAge = now - new Date(event.timestamp || 0).getTime();
    if (eventAge > ACTIVE_WINDOW_MS) {
      return;
    }

    if (ACTIVE_EVENT_TYPES.has(event.type)) {
      if (!active[event.agent.id]) {
        active[event.agent.id] = event;
      }
      return;
    }

    if (event.type === EVENT_TYPES.AGENT_RESPONDED || event.type === EVENT_TYPES.CONVERSATION_CLOSED) {
      delete active[event.agent.id];
    }
  });

  return active;
};

const generateMockEvent = (agents) => {
  const customer = MOCK_CUSTOMERS[Math.floor(Math.random() * MOCK_CUSTOMERS.length)];
  const message = MOCK_MESSAGES[Math.floor(Math.random() * MOCK_MESSAGES.length)];
  const agent = agents[Math.floor(Math.random() * agents.length)];

  return {
    id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: EVENT_TYPES.NEW_MESSAGE,
    timestamp: new Date().toISOString(),
    customer: {
      id: customer.id,
      name: customer.name,
      avatar: customer.avatar,
      channel: customer.channel,
    },
    agent: agent ? {
      id: agent.id,
      name: agent.name,
    } : null,
    data: {
      message,
      conversationId: `conv_${Math.random().toString(36).substr(2, 9)}`,
    },
  };
};

export function useWebhookEvents({
  accountId = null,
  agents = [],
  enabled = true,
  mockInterval = 8000,
  useMockData = false,
} = {}) {
  const [events, setEvents] = useState([]);
  const [activeEvents, setActiveEvents] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const eventTimeoutsRef = useRef({});
  const eventIdsRef = useRef(new Set());
  const latestTimestampRef = useRef(null);

  const shouldUseMockData = useMockData || !accountId;

  const clearScheduledResponse = useCallback((agentId) => {
    if (eventTimeoutsRef.current[agentId]) {
      clearTimeout(eventTimeoutsRef.current[agentId]);
      delete eventTimeoutsRef.current[agentId];
    }
  }, []);

  const addEvent = useCallback((inputEvent, options = {}) => {
    const { scheduleAutoResponse = false } = options;
    const event = normalizeEvent(inputEvent, agents);
    if (!event || eventIdsRef.current.has(event.id)) {
      return null;
    }

    eventIdsRef.current.add(event.id);
    if (!latestTimestampRef.current || new Date(event.timestamp).getTime() > new Date(latestTimestampRef.current).getTime()) {
      latestTimestampRef.current = event.timestamp;
    }

    setEvents((current) => sortEventsDesc([event, ...current]).slice(0, 50));

    if (event.agent?.id) {
      if (ACTIVE_EVENT_TYPES.has(event.type)) {
        setActiveEvents((current) => ({
          ...current,
          [event.agent.id]: event,
        }));
      } else if (event.type === EVENT_TYPES.AGENT_RESPONDED || event.type === EVENT_TYPES.CONVERSATION_CLOSED) {
        clearScheduledResponse(event.agent.id);
        setActiveEvents((current) => {
          const updated = { ...current };
          delete updated[event.agent.id];
          return updated;
        });
      }

      if (scheduleAutoResponse && event.type === EVENT_TYPES.NEW_MESSAGE) {
        const responseTime = 2000 + Math.random() * 3000;
        clearScheduledResponse(event.agent.id);
        eventTimeoutsRef.current[event.agent.id] = setTimeout(() => {
          addEvent({
            id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: EVENT_TYPES.AGENT_RESPONDED,
            timestamp: new Date().toISOString(),
            customer: event.customer,
            agent: event.agent,
            data: {
              conversationId: event.data?.conversationId,
              responseTime: Math.round(responseTime / 1000),
            },
          });
        }, responseTime);
      }
    }

    return event;
  }, [agents, clearScheduledResponse]);

  const clearEvents = useCallback(() => {
    setEvents([]);
    setActiveEvents({});
    eventIdsRef.current = new Set();
    latestTimestampRef.current = null;
    Object.keys(eventTimeoutsRef.current).forEach(clearScheduledResponse);
  }, [clearScheduledResponse]);

  useEffect(() => {
    clearEvents();
  }, [accountId, clearEvents]);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      setError(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return undefined;
    }

    if (shouldUseMockData) {
      const connectTimeout = setTimeout(() => {
        setIsConnected(true);
        setError(null);
      }, 500);

      return () => clearTimeout(connectTimeout);
    }

    let cancelled = false;

    const pollEvents = async (isInitial = false) => {
      try {
        const response = await fetchWebhookEvents(accountId, {
          limit: isInitial ? 50 : 20,
          since: isInitial ? undefined : latestTimestampRef.current || undefined,
        });
        const incoming = response?.events || response?.data?.events || [];
        const normalized = sortEventsDesc(
          incoming
            .map((event) => normalizeEvent(event, agents))
            .filter(Boolean)
        );

        if (cancelled) {
          return;
        }

        if (isInitial) {
          eventIdsRef.current = new Set(normalized.map((event) => event.id));
          latestTimestampRef.current = normalized[0]?.timestamp || null;
          setEvents(normalized.slice(0, 50));
          setActiveEvents(buildActiveEvents(normalized));
        } else {
          normalized
            .slice()
            .reverse()
            .forEach((event) => addEvent(event));
        }

        setIsConnected(true);
        setError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        // Don't show error for 404 (API not available) - just show as disconnected
        console.warn('[useWebhookEvents] Webhook events API not available:', err.message);
        setIsConnected(false);
        // Only set error for non-404 errors
        if (!err.message?.includes('404') && !err.message?.includes('Unable to load events')) {
          setError(err.message || 'Unable to load live events.');
        }
      }
    };

    pollEvents(true);
    intervalRef.current = setInterval(() => {
      pollEvents(false);
    }, REAL_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [accountId, addEvent, agents, enabled, shouldUseMockData]);

  useEffect(() => {
    if (!enabled || !isConnected || !shouldUseMockData || mockInterval <= 0 || agents.length === 0) {
      return undefined;
    }

    const initialTimeout = setTimeout(() => {
      addEvent(generateMockEvent(agents), { scheduleAutoResponse: true });
    }, 2000);

    intervalRef.current = setInterval(() => {
      addEvent(generateMockEvent(agents), { scheduleAutoResponse: true });
    }, mockInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, isConnected, mockInterval, agents, addEvent, shouldUseMockData]);

  useEffect(() => () => {
    Object.keys(eventTimeoutsRef.current).forEach(clearScheduledResponse);
  }, [clearScheduledResponse]);

  const triggerEvent = useCallback((customEvent = null) => {
    const event = customEvent || generateMockEvent(agents);
    addEvent(event, { scheduleAutoResponse: shouldUseMockData });
  }, [agents, addEvent, shouldUseMockData]);

  return {
    events,
    activeEvents,
    isConnected,
    error,
    addEvent,
    clearEvents,
    triggerEvent,
    isUsingMockData: shouldUseMockData,
  };
}

export default useWebhookEvents;
